/* eslint-disable import/extensions */
import { isValidNIE, isValidNIF, isValidPassport, isValidCIF } from '@masfront/common-b2c'
import { format } from 'date-fns'
import { createSelector, createStructuredSelector } from 'reselect'

import provinces from '../components/ContactForm/provinces'
import {
  Customer,
  CustomerInfoFormProps,
  DocType,
  CustomerFormModel,
} from '../model/customer.model'

import { selectIsOffline } from '~/agent/store/agent.selectors'
import { CustomerSegment } from '~/basket/model/common/segment.model'
import {
  filterFalsyValues,
  formatToIsoDate,
  findById,
  compareArrayValues,
} from '~/core/helpers/helpers'
import { RootState } from '~/core/store/root.reducer'
import {
  selectDocumentTypes,
  selectNationalities,
  selectSegments,
  selectLanguages,
} from '~/masterData/store/masterData.selectors'
import { selectQueryParams } from '~/router/store/router.selectors'
import { selectIsPostpaid } from '~/sale/store/sale.selectors'

export const getCustomer = (state: RootState) => state.customer

export const selectCustomerValues = createSelector(getCustomer, (customer) => customer.customer)

export const selectDocId = createSelector(
  selectCustomerValues,
  (customer): Customer['docId'] => customer.docId,
)

export const selectCIF = createSelector(selectCustomerValues, (customer) => customer.cif)

export const selectSegment = createSelector(
  selectCustomerValues,
  (customer): Customer['customerSegment'] => customer.customerSegment,
)

export const selectIsEnterprise = createSelector(selectSegment, (segment) => segment === 'Empresa')

export const selectDocIdTovalidate = createSelector(
  selectDocId,
  selectIsEnterprise,
  selectCIF,
  (docId, isEnterprise, cif): Customer['docId'] => (isEnterprise ? cif : docId) || '',
)

export const selectDocType = createSelector(
  selectDocId,
  selectIsPostpaid,
  (docId, isPostpaid): Customer['docType'] => {
    if (isValidNIF(docId)) return DocType.NIF
    if (isValidNIE(docId)) return DocType.NIE
    if (isValidCIF(docId)) return DocType.CIF
    if (isValidPassport(docId) && !isPostpaid) return DocType.PASS
    return ''
  },
)

export const selectDocTypeToValidate = createSelector(
  selectDocType,
  selectIsEnterprise,
  (docType, isEnterprise): Customer['docType'] => (isEnterprise ? 'CIF' : docType),
)

export const selectNationality = createSelector(
  selectCustomerValues,
  (customer) => customer.nationality,
)

export const selectIsCompanyCustomerSegment = createSelector(
  selectSegment,
  (customerSegment) => customerSegment === CustomerSegment.COMPANY,
)

export const selectCustomerInfoProps = createStructuredSelector<RootState, CustomerInfoFormProps>({
  docType: selectDocType,
  isCompanySegment: selectIsCompanyCustomerSegment,
})

export const selectCustomerInfoFormValues = createSelector(
  selectCustomerValues,
  ({ email, language, technology, docType, ...rest }): CustomerFormModel => {
    return rest
  },
)

export const selectCustomerContactValues = createSelector(
  selectCustomerValues,
  ({ email, language, province }) => {
    return {
      email,
      language,
      province,
    }
  },
)

export const selectCustomerFullName = createSelector(
  selectCustomerValues,
  ({ name, lastName, firstName }) => {
    return lastName ? `${name} ${firstName} ${lastName}` : `${name} ${firstName}`
  },
)

export const selectCustomerProvince = createSelector(
  selectCustomerValues,
  (customer) => customer.province,
)

export const selectBirthDate = createSelector(selectCustomerValues, (customer) => {
  return customer.birthdate ? format(customer.birthdate, 'dd/MM/yyyy') : ''
})

export const selectContactPhone = createSelector(
  selectCustomerValues,
  (customer) => customer.contactPhone,
)

export const selectSegmentLiteral = createSelector(selectSegment, (segment) => {
  switch (segment) {
    case CustomerSegment.RESIDENTIAL:
      return 'Particular'
    case CustomerSegment.COMPANY:
      return 'Empresa'
    case CustomerSegment.FREELANCE:
      return 'Autónomo'
    default:
      return ''
  }
})

export const selectCompanyName = createSelector(
  selectCustomerValues,
  (customer) => customer.companyName,
)

export const selectGender = createSelector(selectCustomerValues, (customer) =>
  customer.gender === 'F' ? 'Femenino' : 'Masculino',
)

export const selectClientExistsLoading = createSelector(getCustomer, (customer) => {
  return customer.clientExists.loading
})

export const selectDoesClientExist = createSelector(getCustomer, (customer) => {
  return customer.clientExists.data
})

export const selectClientSearch = createSelector(getCustomer, (customer) => customer.clientSearch)

export const selectClientSearchWithoutClient = createSelector(
  selectClientSearch,
  (clientSearch) => {
    const { errorMessage } = clientSearch
    return errorMessage?.search('user') !== -1 ? 'CLIENT_SEARCH_WITHOUT_CLIENT' : null
  },
)

export const selectClientSearchInvalidContract = createSelector(
  selectClientSearch,
  selectQueryParams,
  (clientSearch, queryParams) => {
    const { data } = clientSearch
    const validContractParams = ['accountNumber', 'brand', 'document', 'documentType']
    return data?.validContract === false &&
      compareArrayValues(queryParams as [], validContractParams as [])
      ? 'CLIENT_SEARCH_INVALID_CONTRACT'
      : null
  },
)

export const selectCustomerFromClientSearch = createSelector(
  selectDocumentTypes,
  selectNationalities,
  selectSegments,
  selectLanguages,
  selectIsOffline,
  selectClientSearch,
  (documentTypes, nationalities, segments, languages, isOffline, clientSearch) => {
    const { data } = clientSearch

    const clientSearchType = data?.customer ? 'customer' : 'enterprise'

    const customerFieldValues = data?.customer && {
      ...data?.customer,
      birthdate: data?.customer?.birthdate ? formatToIsoDate(data?.customer?.birthdate) : '',
      docType: findById(documentTypes, data?.customer?.docType?.id),
      nationality: findById(nationalities, data?.customer?.nationality?.id),
      customerSegment: findById(segments, data?.customer?.customerSegment?.id),
      language: findById(languages, data?.customer?.language?.id),
      province: isOffline
        ? provinces.find(
            (province) => province.id === data?.customer?.address?.billing?.provinceId.toString(),
          )
        : '',
      address: '',
    }

    const enterpriseFieldValues = data?.enterprise && {
      ...data?.enterprise,
      ...data?.enterprise?.responsable,
      docType: findById(documentTypes, data?.enterprise?.responsable?.docType?.id),
      nationality: findById(nationalities, data?.enterprise?.nationality?.id),
      customerSegment: findById(segments, data?.enterprise?.customerSegment?.id),
      language: findById(languages, data?.enterprise?.language?.id),
      province: isOffline
        ? provinces.find(
            (province) => province.id === data?.enterprise?.address?.billing?.provinceId.toString(),
          )
        : '',
      address: '',
    }

    const clientSearchFieldValues = filterFalsyValues({
      customer: customerFieldValues,
      enterprise: enterpriseFieldValues,
    })

    return clientSearchFieldValues[clientSearchType as keyof typeof clientSearchFieldValues]
  },
)
